import {noop, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import EntryActions from 'actions/EntryActions';
import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SequencePrime} from 'biojs/BioConstants';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {POLYMER_SUBTYPE_TO_POLYMER_TYPE, POLYMER_SUBTYPE_TO_MODEL_TYPE} from 'constants/MolbioConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import PerformAssemblyModal from 'molbio/bulk-assembly/assemble-modal/PerformAssemblyModal';
import PerformConcatenationModal from 'molbio/bulk-assembly/assemble-modal/PerformConcatenationModal';
import {
  FragmentDetails,
  RawOutputPolymerCount,
  UnknownInputPolymers,
} from 'molbio/bulk-assembly/assemble-modal/polymerSchemaLinkCompatibility';
import RevampedPerformAssemblyModal from 'molbio/bulk-assembly/assemble-modal/RevampedPerformAssemblyModal';
import {AssemblyCoordinator} from 'molbio/bulk-assembly/AssemblyCoordinator';
import {
  getLegacyBulkAssemblyBannerProps,
  getBulkAssemblyArchivedBannerProps,
} from 'molbio/bulk-assembly/Banners';
import {
  BulkAssemblyMethod,
  METHOD_TO_DISPLAY,
  SettingsModalBulkAssemblyParams,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {
  FinalizationLocationInfo,
  finalizeAssembly,
  SaveInformation,
  sendBulkAssemblyToNotebook as sendBulkAssemblyToNotebookDAO,
} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import {shouldSkipBulkAssemblyValidation} from 'molbio/bulk-assembly/BulkAssemblyValidation';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {BAIdValue} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {CloningSetupModal} from 'molbio/bulk-assembly/setup-modal/CloningSetupModal';
import {ConcatenationSetupModal} from 'molbio/bulk-assembly/setup-modal/ConcatenationSetupModal';
import {isProteinBasedAssembly} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Badge, {BadgeColor} from 'ui-library/Badge';
import {InlineBannerActionType, InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK} from 'ui-library/Colors';
import {Header2} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import {listToReadable} from 'util/NameUtil';
import {isServerRequestError} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';
import {
  resumeTaskPromise,
  OngoingTaskResponse,
  FailedTaskError,
  isOngoingTaskResponse,
} from 'util/TaskRunner';
import {LOADING} from 'util/Types';

export interface BulkAssemblyHeaderProps {
  assemblyCoordinator: AssemblyCoordinator;
  onSetName: (name: string) => Promise<void>;
  onSetFolder: (folderId: string) => void;
}

/**
 * Build a list of banners to display at the top of the assembly overview tab.
 * @param assemblyCoordinator
 */
function getAssemblyBanners(
  assemblyCoordinator: AssemblyCoordinator,
  showLegacyAssemblyBanner: boolean,
  setShowLegacyAssemblyBanner: (show: boolean) => void
): Array<InlineBannerProps> {
  const banners: Array<InlineBannerProps> = [];
  if (assemblyCoordinator.isArchived) {
    banners.push(
      getBulkAssemblyArchivedBannerProps(
        assemblyCoordinator.assemblyId,
        Boolean(assemblyCoordinator.permissions.unarchivable)
      )
    );
  }
  if (!assemblyCoordinator.permissions?.writable) {
    banners.push({
      bannerType: InlineBannerType.INFO,
      text: 'Editing disabled on read-only entity',
    });
  }
  if (!assemblyCoordinator.assemblySelector.isConcatenation() && showLegacyAssemblyBanner) {
    banners.push({
      ...getLegacyBulkAssemblyBannerProps(noop),
      onDismiss: () => {
        setShowLegacyAssemblyBanner(false);
      },
    });
  }

  if (assemblyCoordinator.isArchived) {
    return banners;
  }

  if (assemblyCoordinator.isFinalized) {
    banners.push({
      bannerType: InlineBannerType.INFO,
      text: 'This is a read-only record of a finalized assembly.',
      dataTestAttrs: {component: 'BulkAssemblyOverviewTab', element: 'finalizedBanner'},
    });
  }

  const assemblyResult = assemblyCoordinator.validationResults.assemblyResult;
  if (assemblyResult && assemblyResult !== LOADING) {
    banners.push(
      ...assemblyResult.validationMessages
        .filter(
          (message) =>
            message.status === ValidationStatus.ERROR || message.status === ValidationStatus.WARNING
        )
        .map((message): InlineBannerProps => {
          if (message.supportingInfo) {
            return {
              bannerType: InlineBannerType[message.status],
              text: message.message,
              supportingInfo: message.supportingInfo,
              action: {
                actionType: InlineBannerActionType.ACCORDION,
              },
            };
          } else {
            return {
              bannerType: InlineBannerType[message.status],
              text: message.message,
            };
          }
        })
    );
  }
  return banners;
}

/**
 * Run the finalize endpoint, refreshing data as necessary.
 *
 * If the assembly succeeds, it will have transitioned to a finalized state with the validation
 * results in the assembly object itself, so we should re-fetch the assembly.
 *
 * If the assembly fails, there must be some new error like a concurrently-edited sequence, so
 * re-fetch the validation so the user can see any new problems.
 *
 * Exported for testing.
 */
export async function finalizeAssemblyFromInput(
  assemblyCoordinator: AssemblyCoordinator,
  fragmentSaveInfo: SaveInformation | null,
  constructSaveInfo: SaveInformation,
  primerSaveInfo: SaveInformation | null
): Promise<void> {
  let response: {} | OngoingTaskResponse;
  try {
    response = await finalizeAssembly({
      bulkAssemblyId: assemblyCoordinator.assemblyId,
      fragmentSaveInfo,
      constructSaveInfo,
      primerSaveInfo,
      procedureJson: assemblyCoordinator.state,
    });
  } catch (e) {
    if (!isServerRequestError(e)) {
      throw e;
    }

    notifications.notifyError(e);
    await assemblyCoordinator.doRefetchValidation();
    return;
  }

  if (isOngoingTaskResponse(response)) {
    await _handleNotebookTaskPolling(
      response,
      assemblyCoordinator,
      constructSaveInfo,
      fragmentSaveInfo,
      primerSaveInfo
    );
  }

  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.COMPLETED_BULK_ASSEMBLY_FINALIZATION, {
    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
    assemblyId: assemblyCoordinator.assemblyId,
    cloningMethod: assemblyCoordinator.assemblySelector.getMethod().type,
  });

  const folderIds = new Set([constructSaveInfo.folderId]);
  for (const maybeSaveInfo of [primerSaveInfo, fragmentSaveInfo]) {
    if (maybeSaveInfo) {
      folderIds.add(maybeSaveInfo.folderId);
    }
  }

  folderIds.forEach((folderId) => {
    FolderDAOCache.invalidateFolderContents(folderId);
  });

  await assemblyCoordinator.doRefetchAssembly();
}

async function _handleNotebookTaskPolling(
  notebookTaskResponse: OngoingTaskResponse,
  assemblyCoordinator: AssemblyCoordinator,
  constructSaveInfo: SaveInformation,
  fragmentSaveInfo: SaveInformation | null,
  primerSaveInfo: SaveInformation | null
): Promise<void> {
  try {
    await resumeTaskPromise(notebookTaskResponse.status_url);
    _showSendToNotebookSuccess(constructSaveInfo, fragmentSaveInfo, primerSaveInfo);
  } catch (e) {
    if (!isServerRequestError(e)) {
      throw e;
    }
    _showSendToNotebookError(assemblyCoordinator, constructSaveInfo, fragmentSaveInfo, primerSaveInfo, e);
  }
}

/**
 * Show a success notification when the assembly is sucessfully sent to an entry, along with a link to open the entry.
 */
function _showSendToNotebookSuccess(
  constructSaveInfo: SaveInformation,
  fragmentSaveInfo: SaveInformation | null,
  primerSaveInfo: SaveInformation | null
): void {
  const hasFragments = Boolean(fragmentSaveInfo?.tableOptions);
  const hasPrimers = Boolean(primerSaveInfo?.tableOptions);

  const items = ['Constructs'];
  if (hasFragments) {
    items.push('fragments');
  }
  if (hasPrimers) {
    items.push('primers');
  }

  notifications.success(`${listToReadable(items)} successfully added to entry.`, {
    duration: 0,
    actionText: 'View all',
    onActionClick: async (): Promise<void> => {
      await EntryActions.open(constructSaveInfo.documentLikeId!); // load the most recent version of the entry
      FileBrowserActions.openItem(constructSaveInfo.documentLikeId!);
    },
  });
}

/**
 * Show an error notification when the assembly fails to send to an entry, along with a retry button that retries just
 * sending the assembly to an entry.
 */
function _showSendToNotebookError(
  assemblyCoordinator: AssemblyCoordinator,
  constructSaveInfo: SaveInformation,
  fragmentSaveInfo: SaveInformation | null,
  primerSaveInfo: SaveInformation | null,
  error: FailedTaskError
): void {
  let message = 'Sending to notebook failed.';
  if (error.status === 409) {
    message = error.userMessage?.replace('Please refresh the page and try again later.', '') ?? message;
  }

  notifications.error(message, {
    duration: 0,
    actionText: 'Retry',
    onActionClick: async (): Promise<void> => {
      await sendBulkAssemblyToNotebook(
        assemblyCoordinator,
        constructSaveInfo,
        fragmentSaveInfo,
        primerSaveInfo
      );
    },
  });
}

async function sendBulkAssemblyToNotebook(
  assemblyCoordinator: AssemblyCoordinator,
  constructSaveInfo: SaveInformation,
  fragmentSaveInfo: SaveInformation | null,
  primerSaveInfo: SaveInformation | null
): Promise<void> {
  try {
    await sendBulkAssemblyToNotebookDAO({
      bulkAssemblyId: assemblyCoordinator.assemblyId,
      documentLikeId: constructSaveInfo.documentLikeId!,
      constructTableOptions: constructSaveInfo.tableOptions!,
      fragmentTableOptions: fragmentSaveInfo?.tableOptions,
      primerTableOptions: primerSaveInfo?.tableOptions,
    });
    _showSendToNotebookSuccess(constructSaveInfo, fragmentSaveInfo, primerSaveInfo);
  } catch (e) {
    if (!isServerRequestError(e)) {
      throw e;
    }
    _showSendToNotebookError(assemblyCoordinator, constructSaveInfo, fragmentSaveInfo, primerSaveInfo, e);
  }
}

/**
 * Open the finalize modal and, if accepted, perform finalize.
 */
async function handleAssembleButtonClick(assemblyCoordinator: AssemblyCoordinator): Promise<void> {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_FINALIZATION_MODAL, {
    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
    assemblyId: assemblyCoordinator.assemblyId,
  });
  let result: FinalizationLocationInfo | null;
  const constructCount = assemblyCoordinator.getNumConstructs();
  if (BenchlingConfig.MOLBIO_ENABLE_ASSEMBLY_SCHEMA_FIELD_MAPPING) {
    const assemblyMethod = assemblyCoordinator.assemblySelector.getMethod();
    const constructOutputDetails: RawOutputPolymerCount = {
      type: 'RAW_OUTPUT_POLYMER_COUNT',
      modelType:
        POLYMER_SUBTYPE_TO_MODEL_TYPE[
          assemblyCoordinator.assemblySelector.getSupportedPolymerConstructType()
        ],
      count: constructCount,
    };

    const primerOutputDetails: RawOutputPolymerCount = {
      type: 'RAW_OUTPUT_POLYMER_COUNT',
      modelType: ModelTypeConstants.DNA_OLIGO,
      count: assemblyCoordinator.getNumDesignedOligos(),
    };
    const canCreateFragments = assemblyMethod.type !== BulkAssemblyMethod.CONCATENATION;
    const fragmentBins = assemblyCoordinator.assemblySelector.getFragmentBins();
    const fragmentDetailsByBin: Record<string, FragmentDetails> = objectFromEntries(
      fragmentBins.map((bin) => {
        const outputDetails: RawOutputPolymerCount = {
          type: 'RAW_OUTPUT_POLYMER_COUNT',
          modelType: isProteinBasedAssembly(assemblyMethod)
            ? ModelTypeConstants.PROTEIN
            : ModelTypeConstants.SEQUENCE,
          count: 0,
        };
        if (canCreateFragments) {
          // Amplified fragments are created per input fragment per construct. Note that this may be an overestimate
          // because the backend may de-deduplicate the amplified fragments but that only happens upon finalization.
          outputDetails.count =
            assemblyCoordinator.specifiedFragmentInfosByBinCid[bin.cid].length * constructCount;
        }
        return [
          bin.cid,
          {
            inputDetails: {
              type: 'UNKNOWN_INPUT_POLYMERS',
              polymerIds: assemblyCoordinator.specifiedFragmentInfosByBinCid[bin.cid].map(({fragmentCid}) => {
                if (isProteinBasedAssembly(assemblyMethod)) {
                  return (assemblyCoordinator.fragmentsByCid[fragmentCid].protein as BAIdValue).id;
                } else {
                  return (assemblyCoordinator.fragmentsByCid[fragmentCid].sequence as BAIdValue).id;
                }
              }),
            },
            outputDetails,
          },
        ];
      })
    );

    const primerDetailsByPrime: Record<
      SequencePrime,
      {preferredPrimers: UnknownInputPolymers; designedPrimers: RawOutputPolymerCount}
    > = {
      [SequencePrime.FIVE]: {
        preferredPrimers: {
          polymerIds: assemblyCoordinator.getPreferredPrimerIds(SequencePrime.FIVE),
          type: 'UNKNOWN_INPUT_POLYMERS',
        },
        designedPrimers: {
          type: 'RAW_OUTPUT_POLYMER_COUNT',
          modelType: ModelTypeConstants.DNA_OLIGO,
          count: assemblyCoordinator.getNumDesignedOligos(SequencePrime.FIVE),
        },
      },
      [SequencePrime.THREE]: {
        preferredPrimers: {
          polymerIds: assemblyCoordinator.getPreferredPrimerIds(SequencePrime.THREE),
          type: 'UNKNOWN_INPUT_POLYMERS',
        },
        designedPrimers: {
          type: 'RAW_OUTPUT_POLYMER_COUNT',
          modelType: ModelTypeConstants.DNA_OLIGO,
          count: assemblyCoordinator.getNumDesignedOligos(SequencePrime.THREE),
        },
      },
    };

    result = await runModal(RevampedPerformAssemblyModal, {
      currentUserId: EntityStore.currentUser().id,
      initialFolderId: assemblyCoordinator.folderId,
      canCreatePrimers: assemblyCoordinator.canCreatePrimers(),
      canCreateFragments,
      fragmentIcon: assemblyCoordinator.getFragmentIcon(),
      constructOutputDetails,
      primerOutputDetails,
      orderedBinDetails: fragmentBins.map((bin) => ({
        binId: bin.cid,
        binName: bin.name,
        binColor: bin.color,
      })),
      fragmentDetailsByBin,
      primerDetailsByPrime,
    });
  } else {
    const finalizationModalProps = {
      currentUserId: EntityStore.currentUser().id,
      initialFolderId: assemblyCoordinator.folderId,
      canCreatePrimers: assemblyCoordinator.canCreatePrimers(),
      numConstructs: constructCount,
    };

    if (assemblyCoordinator.assemblySelector.getMethod().type === BulkAssemblyMethod.CONCATENATION) {
      result = await runModal(PerformConcatenationModal, {
        ...finalizationModalProps,
        outputConstructSubtype: assemblyCoordinator.assemblySelector.getSupportedPolymerConstructType(),
      });
    } else {
      result = await runModal(PerformAssemblyModal, finalizationModalProps);
    }
  }
  if (result != null) {
    const {fragmentSaveInfo, constructSaveInfo, primerSaveInfo} = result;
    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.SUBMITTED_BULK_ASSEMBLY_FINALIZATION_MODAL, {
      actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_FINALIZATION_MODAL,
      assemblyId: assemblyCoordinator.assemblyId,
      notebookEntrySelected: Boolean(constructSaveInfo.documentLikeId),
      constructTableDestination: constructSaveInfo.tableOptions?.tableDestination || null,
      primerTableDestination: primerSaveInfo?.tableOptions?.tableDestination || null,
      fragmentTableDestination: fragmentSaveInfo?.tableOptions?.tableDestination || null,
    });
    const shouldSkipValidation = shouldSkipBulkAssemblyValidation(
      assemblyCoordinator.state,
      assemblyCoordinator.polymerData
    );
    assert(!shouldSkipValidation, 'Expected to receive validation input');
    await withLoadingScreen(
      finalizeAssemblyFromInput(assemblyCoordinator, fragmentSaveInfo, constructSaveInfo, primerSaveInfo),
      'Finalizing...'
    );
  }
}

async function handleEditBulkAssemblySettingsClick(
  assemblyCoordinator: AssemblyCoordinator,
  onSetName: (name: string) => Promise<void>,
  onSetFolder: (folderId: string) => void
): Promise<void> {
  const methodData = assemblyCoordinator.assemblySelector.getMethod();
  const commonProps = {
    initialFolderId: assemblyCoordinator.folderId,
    initialName: assemblyCoordinator.name,
    initialTopology: assemblyCoordinator.topology,
    isInitialSetup: false,
    initialNumFragmentBins: assemblyCoordinator.assemblySelector.getNumFragmentBins(),
    canEditAssembly: !assemblyCoordinator.isReadOnly(),
    canEditFolder: Boolean(!assemblyCoordinator.isArchived && assemblyCoordinator.permissions.movable),
    canEditName: Boolean(!assemblyCoordinator.isArchived && assemblyCoordinator.permissions.writable),
    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
  };
  let newAssemblyParams: SettingsModalBulkAssemblyParams | null;
  if (methodData.type !== BulkAssemblyMethod.CONCATENATION) {
    newAssemblyParams = await runModal(CloningSetupModal, {
      ...commonProps,
      initialMethodData: methodData,
      fragmentProductionMethodOverride: assemblyCoordinator.getFragmentProductionMethod(),
    });
  } else {
    newAssemblyParams = await runModal(ConcatenationSetupModal, {
      ...commonProps,
      initialMethodData: methodData,
    });
  }

  if (newAssemblyParams != null) {
    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.SAVED_BULK_ASSEMBLY_SETTINGS, {
      actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
      topology: newAssemblyParams.procedureJson.topology,
      parameters: newAssemblyParams.procedureJson.method,
      assemblyId: assemblyCoordinator.assemblyId,
    });
    // We only care about the name, folder, and method on an update; the rest should be locked.
    if (newAssemblyParams.name !== assemblyCoordinator.name) {
      await onSetName(newAssemblyParams.name);
    }
    if (newAssemblyParams.folderId !== assemblyCoordinator.folderId) {
      onSetFolder(newAssemblyParams.folderId);
    }
    if (!assemblyCoordinator.isReadOnly()) {
      assemblyCoordinator.updateMethodParametersAndTopology(
        newAssemblyParams.procedureJson.method.parameters,
        newAssemblyParams.procedureJson.topology
      );
    }
  }
}

export default function BulkAssemblyHeader({
  assemblyCoordinator,
  onSetName,
  onSetFolder,
}: BulkAssemblyHeaderProps): JSX.Element {
  const [showLegacyAssemblyBanner, setShowLegacyAssemblyBanner] = useState(true);
  const assemblyBanners = getAssemblyBanners(
    assemblyCoordinator,
    showLegacyAssemblyBanner,
    setShowLegacyAssemblyBanner
  );
  const finalizationErrorMessage = assemblyCoordinator.getAssemblyFinalizationErrorMessage();
  const methodData = assemblyCoordinator.assemblySelector.getMethod();
  let methodDisplayName: string;
  let finalizeVerb: string;
  if (methodData.type === BulkAssemblyMethod.CONCATENATION) {
    finalizeVerb = 'Concatenate';
    methodDisplayName = `${POLYMER_SUBTYPE_TO_POLYMER_TYPE[methodData.parameters.polymerSubtype]} ${
      METHOD_TO_DISPLAY[methodData.type]
    }`;
  } else {
    finalizeVerb = 'Assemble';
    methodDisplayName = METHOD_TO_DISPLAY[methodData.type];
  }
  return (
    <>
      <div className={css(styles.header)}>
        <div className={css(styles.assemblyInfo)}>
          <Header2
            className={css(styles.assemblyName)}
            dataTestAttrs={{component: 'BulkAssemblyOverviewTab', element: 'nameHeader'}}
          >
            {assemblyCoordinator.name}
          </Header2>
          <Badge
            color={BadgeColor.GRAY}
            className={css(styles.badge)}
            dataTestAttrs={{component: 'BulkAssemblyOverviewTab', element: 'methodBadge'}}
          >
            {methodDisplayName}
          </Badge>
          <BenchlingIcon
            className={css(styles.settings)}
            onClick={() => {
              handleEditBulkAssemblySettingsClick(assemblyCoordinator, onSetName, onSetFolder);
            }}
            icon="settings"
          />
        </div>
        <div className={css(styles.headerButtons)}>
          <ButtonLink
            text="Provide feedback"
            buttonStyle={ButtonStyle.LINK}
            href="https://forms.gle/X5TKRdYQ7gvLCTD5A"
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.provideFeedbackButton)}
          />
          {!assemblyCoordinator.isFinalized && !assemblyCoordinator.isArchived && (
            <Tooltip disabled={finalizationErrorMessage == null} title={finalizationErrorMessage}>
              <Button
                text={finalizeVerb}
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.MINI}
                disabled={finalizationErrorMessage != null}
                onClick={() => handleAssembleButtonClick(assemblyCoordinator)}
                dataTestAttrs={{component: 'BulkAssemblyOverviewTab', element: 'assembleButton'}}
              />
            </Tooltip>
          )}
        </div>
      </div>
      {assemblyBanners.length > 0 && (
        <div className={css(styles.assemblyBanners)}>
          <InlineBannerStack messages={assemblyBanners} />
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    marginBottom: '16px',
    justifyContent: 'space-between',
  },
  settings: {
    color: GRAY_DARK,
    margin: 'auto 0 auto 8px',
    cursor: 'pointer',
  },
  badge: {
    marginLeft: '8px',
  },
  assemblyName: {
    marginBottom: 0,
  },
  assemblyBanners: {
    marginBottom: 36,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'baseline',
    flex: '0 0 auto',
  },
  provideFeedbackButton: {
    ...FONT_PROXIMA_13,
    ':focus': {textDecoration: 'none'},
    marginRight: '8px',
  },
  assemblyInfo: {
    display: 'flex',
    alignItems: 'baseline',
  },
});
